import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';

import aiScanAnimation from '../assets/lottie/ai-ticket.json';
import pollinAnimation from '../assets/lottie/pollin-axm.json';
import GTT from '../assets/GUESTVERSITY-PNG.png';
import Masriq from '../assets/MASRIQ.png';
import EventMate from '../assets/EventMateTabIcon.png';
import PTT from '../assets/PTT.png';

const workItems = [
  {
    animation: pollinAnimation,
    title: 'Poll-In Platform',
    description: 'Poll-In is an interactive platform built with HTML5, CSS3, JavaScript, Flask, MySQL, and Python. Designed for ArtistXMusic, it enables live polls, quizzes, and real-time community chats, offering seamless engagement through a modern, responsive UI..',
  },
  {
    logo: Masriq,
    title: 'MASRIQ NextTech Solutions Website',
    description: 'The MASRIQ official website, developed using React Native, Node.js, PostgreSQL, Google Sheets API, Netlify, Render, and Cloudinary, highlights our AI, cloud, and software expertise. It delivers fast performance, responsive design, and a seamless digital experience.',
    link: 'https://masriq.tech'
  },
  {
    logo: EventMate,
    title: 'EventMate-Ai Website',
    description: 'The EventMate-AI website, built with React Native, Node.js, PostgreSQL, Google Sheets API, and hosted on Netlify and Render, showcases our AI-driven event management platform. Optimized with Cloudinary for media handling, it provides a sleek, intuitive interface for event organizers and attendees.',
    link: 'https://eventmate-ai.com'
  },
  {
    animation: aiScanAnimation,
    title: 'AI Ticket Scanner',
    description: 'MASRIQ AI Ticket Scanner uses Ollama, Llama 3, and Python to extract RSVP and travel details from tickets. This intelligent event tool automates guest management, enhances accuracy, and streamlines operations through advanced machine learning and natural language processing.',
  },
  {
    logo: EventMate,
    title: 'EventMate-Ai Application',
    description: 'The EventMate-AI mobile app, developed with React Native, Node.js, PostgreSQL, Google Sheets API, and integrated with WhatsApp Cloud API, Meta Business Provider, and Google Drive API, empowers organizers with AI-powered scheduling, automated RSVPs, and smart guest engagement.',
    link: 'https://eventmate-ai.com'
  },
  {
    logo: GTT,
    title: 'Guestversity Tours N Travels',
    description: 'Guestversity Tours & Travels website, built using HTML5, CSS3, and JavaScript, delivers a modern travel booking experience. It features curated itineraries, personalized travel planning, and responsive design optimized for speed and user engagement.',
    link: 'https://gttravels.in'
  },
  {
    logo: PTT,
    title: 'Prasanna Tours & Travels',
    description: 'Prasanna Tours & Travels website is crafted with HTML5, CSS3, and JavaScript to provide customers with customized journeys, secure bookings, and an elegant travel platform. Designed for simplicity and performance, it ensures a smooth online travel experience.',
    link: 'https://prasannatravel.com'
  }
];

<img
  src={workItems.logo}
  alt={`${workItems}.title} – MASRIQ Project`}
  title={`${workItems}.title} – MASRIQ Project`}
  className="h-32 w-auto object-contain pointer-events-none"
/>

const OurWorks = () => {
  useEffect(() => {
    AOS.init({ once: true, duration: 1000, easing: 'ease-in-out' });
  }, []);

  return (
    <section
      id="ourworks"
      className="scroll-mt-24 snap-start bg-[#0B0C1C] py-24 px-6 md:px-16 lg:px-32"
    >
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-4xl sm:text-5xl font-extrabold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-blue-500 mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Our Works
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {workItems.map((item, index) => {
            const isGuestversity = !!item.logo && !!item.link;
            const CardContent = (
              <>
                <div className="relative h-44 w-full flex items-center justify-center overflow-hidden mb-4">
                  {isGuestversity ? (
                    <img
                      src={item.logo}
                      alt={item.title}
                      className="h-32 w-auto object-contain pointer-events-none"
                    />
                  ) : (
                    <Lottie
                      animationData={item.animation}
                      loop
                      autoplay
                      className="h-full w-full object-contain pointer-events-none"
                      rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
                    />
                  )}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-purple-700 mb-2 group-hover:text-blue-600 transition">
                  {item.title}
                </h3>
                <p className="text-gray-400 text-sm sm:text-base leading-relaxed group-hover:text-gray-200">{item.description}</p>
                <div className="absolute bottom-0 left-0 h-1 w-full opacity-0 group-hover:opacity-100 bg-gradient-to-r from-purple-400 to-blue-500 transition-all duration-500 rounded-bl-3xl rounded-br-3xl" />
              </>
            );

            return isGuestversity ? (
              <motion.a
                key={index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                 aria-label={`Project: ${item.title}. ${item.description}`}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="bg-[#0F172A] rounded-3xl p-6 border border-purple-100 shadow-xl backdrop-blur-xl transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 group relative overflow-hidden cursor-pointer"
              >
                {CardContent}
              </motion.a>
            ) : (
              <motion.div
                key={index}
                tabIndex={0}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="bg-[#0F172A] rounded-3xl p-6 border border-purple-100 shadow-xl backdrop-blur-xl transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 group relative overflow-hidden"
              >
                {CardContent}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OurWorks;
